import { formatDateUSShort } from "../../Utils/Helper/Helper";
import { sortQuarterPeriodsDesc } from "../../Utils/Helper/sponsorEngagementUi";

export type PollAccordionTabId = "poll1" | "poll2";

export type DiscoveryHubPollHubTab = "ongoing" | "late-window" | "completed";

export const formatDiscoveryHubDate = (
  value: string | null | undefined
): string => {
  if (!value) return "—";
  try {
    return formatDateUSShort(value, true);
  } catch {
    return value;
  }
};

export type TeamPoll2Row = {
  team_member_id: string;
  team_member_name: string;
  sponsor_id: string;
  sponsor_name: string;
  cycle_id: string;
  total_accounts: number;
  completed_accounts: number;
  feedback_status?: string;
  has_overdue?: boolean;
  days_overdue?: number;
  reminder_count?: number;
  last_reminded_at?: string | null;
};

export const getPollGroupRows = (
  group: Record<string, unknown>,
  hubTab: DiscoveryHubPollHubTab,
  pollTab: PollAccordionTabId
): any[] => {
  if (pollTab === "poll2") {
    if (hubTab === "completed") {
      const completed = group.completed_team_poll2;
      const overdue = group.overdue_team_poll2;
      return [
        ...(Array.isArray(completed) ? completed : []),
        ...(Array.isArray(overdue) ? overdue : []),
      ];
    }
    if (hubTab === "ongoing") {
      return [
        ...(Array.isArray(group.ongoing_team_poll2)
          ? group.ongoing_team_poll2
          : []),
        ...(Array.isArray(group.completed_team_poll2)
          ? group.completed_team_poll2
          : []),
      ];
    }
    return [
      ...(Array.isArray(group.late_window_team_poll2)
        ? group.late_window_team_poll2
        : []),
      ...(Array.isArray(group.completed_team_poll2)
        ? group.completed_team_poll2
        : []),
    ];
  }

  if (hubTab === "completed") {
    const completed = group.completed_sponsors;
    const overdue = group.overdue_sponsors;
    const ongoing = group.ongoing_sponsors;
    return [
      ...(Array.isArray(completed) ? completed : []),
      ...(Array.isArray(overdue) ? overdue : []),
      ...(Array.isArray(ongoing) ? ongoing : []),
    ];
  }

  if (hubTab === "ongoing") {
    return [
      ...(Array.isArray(group.ongoing_sponsors) ? group.ongoing_sponsors : []),
      ...(Array.isArray(group.completed_sponsors)
        ? group.completed_sponsors
        : []),
    ];
  }

  return [
    ...(Array.isArray(group.late_window_sponsors)
      ? group.late_window_sponsors
      : []),
    ...(Array.isArray(group.completed_sponsors)
      ? group.completed_sponsors
      : []),
  ];
};

type PollHubStatus = "ongoing" | "late_window" | "completed";

const pollGroupHubStatus = (group: Record<string, unknown>): PollHubStatus => {
  const status = group.hub_status;
  if (
    status === "late_window" ||
    status === "completed" ||
    status === "ongoing"
  ) {
    return status;
  }
  return "ongoing";
};

export const groupMatchesHubTab = (
  group: Record<string, unknown>,
  hubTab: DiscoveryHubPollHubTab
): boolean => {
  const status = pollGroupHubStatus(group);
  if (hubTab === "ongoing") return status === "ongoing";
  if (hubTab === "late-window") return status === "late_window";
  if (hubTab === "completed") {
    return (
      status === "completed" &&
      !group.included_in_draft_leaderboard &&
      !group.published_to_leaderboard &&
      group.phase !== "publish"
    );
  }
  return false;
};

const TAB_SPONSOR_LIST_KEYS: Record<DiscoveryHubPollHubTab, string[]> = {
  ongoing: ["ongoing_sponsors", "completed_sponsors"],
  "late-window": ["late_window_sponsors", "completed_sponsors"],
  completed: ["completed_sponsors", "overdue_sponsors", "ongoing_sponsors"],
};

export const countSponsorsForHubTab = (
  group: Record<string, unknown>,
  hubTab: DiscoveryHubPollHubTab
): number => {
  const seen = new Set<string>();
  for (const key of TAB_SPONSOR_LIST_KEYS[hubTab]) {
    const rows = group[key];
    if (!Array.isArray(rows)) continue;
    for (const row of rows) {
      const sponsorId = (row as { sponsor_id?: string })?.sponsor_id;
      if (sponsorId) seen.add(sponsorId);
    }
  }
  return seen.size;
};

export const buildTabSponsorBreakdown = (
  pollGroups: Record<string, unknown>[],
  hubTab: DiscoveryHubPollHubTab
): Record<string, number> => {
  const counts = {
    not_started: 0,
    in_progress: 0,
    submitted_on_time: 0,
    submitted_late: 0,
    overdue: 0,
  };
  const keys = TAB_SPONSOR_LIST_KEYS[hubTab];
  for (const group of pollGroups) {
    if (!groupMatchesHubTab(group, hubTab)) continue;
    for (const key of keys) {
      const rows = group[key];
      if (!Array.isArray(rows)) continue;
      for (const row of rows) {
        const status = (row as { response_status?: string })?.response_status;
        if (status === "not_started") counts.not_started += 1;
        else if (status === "in_progress") counts.in_progress += 1;
        else if (status === "submitted_on_time") counts.submitted_on_time += 1;
        else if (status === "submitted_late") counts.submitted_late += 1;
        else if (status === "overdue") counts.overdue += 1;
        else counts.not_started += 1;
      }
    }
  }
  const total =
    counts.not_started +
    counts.in_progress +
    counts.submitted_on_time +
    counts.submitted_late +
    counts.overdue;
  return { ...counts, total };
};

export const sponsorPoll1IsComplete = (row: {
  total_accounts?: number;
  completed_accounts?: number;
}): boolean => {
  const total = row.total_accounts ?? 0;
  const done = row.completed_accounts ?? 0;
  return total > 0 && done >= total;
};

export const teamMemberHasPendingResponse = (row: TeamPoll2Row): boolean => {
  const total = row.total_accounts ?? 0;
  const done = row.completed_accounts ?? 0;
  return total > 0 && done < total;
};

export const teamFeedbackStatusLabel = (row: TeamPoll2Row): string => {
  const done = row.completed_accounts ?? 0;
  const total = row.total_accounts ?? 0;
  const status = row.feedback_status;

  if (status === "complete" || (total > 0 && done >= total)) {
    return "Complete";
  }
  if (status === "overdue" || row.has_overdue) {
    const days = row.days_overdue ?? 0;
    return days > 0 ? `Overdue (${days}d)` : "Overdue";
  }
  if (status === "in_progress" || (done > 0 && done < total)) {
    return `In progress (${done}/${total})`;
  }
  return total > 0 ? `Not started (${done}/${total})` : "Not started";
};

export const emptyCopyForPollTab = (
  hubTab: DiscoveryHubPollHubTab,
  pollTab: PollAccordionTabId
): { title: string; hint: string } => {
  if (pollTab === "poll2") {
    if (hubTab === "late-window") {
      return {
        title: "No overdue team feedback",
        hint: "No account team members with overdue Poll 2 feedback for this poll.",
      };
    }
    if (hubTab === "completed") {
      return {
        title: "No completed team feedback",
        hint: "Account team members appear here when feedback is submitted for all of their assigned accounts.",
      };
    }
    return {
      title: "No team feedback participants",
      hint: "Account team members on the roster for this poll appear here, including those who have already submitted.",
    };
  }

  if (hubTab === "late-window") {
    return {
      title: "No pending sponsors in late window",
      hint: "Incomplete sponsors appear here while the late window is still open.",
    };
  }
  if (hubTab === "completed") {
    return {
      title: "No sponsor responses to show",
      hint: "Submitted and non-responded participants appear here once the poll is completed.",
    };
  }
  return {
    title: "No sponsor participants",
    hint: "Sponsors assigned to this poll appear here, including those who have already submitted Poll 1.",
  };
};

type PollGroupLaunchSortable = {
  created_at?: string | null;
  assigned_at?: string | null;
  cycle_id?: string | null;
};

/** Latest created/launched polls first. */
export function sortPollGroupsByLaunchDate<T extends PollGroupLaunchSortable>(
  groups: T[]
): T[] {
  return [...groups].sort((a, b) => {
    const aCreated = a.created_at ?? "";
    const bCreated = b.created_at ?? "";
    if (aCreated !== bCreated) return bCreated.localeCompare(aCreated);
    const aAssigned = a.assigned_at ?? "";
    const bAssigned = b.assigned_at ?? "";
    if (aAssigned !== bAssigned) return bAssigned.localeCompare(aAssigned);
    return (b.cycle_id ?? "").localeCompare(a.cycle_id ?? "");
  });
}

/** Group poll cards by period, newest quarters first. */
export function groupPollGroupsByPeriod<
  T extends PollGroupLaunchSortable & { period?: string },
>(groups: T[]): { period: string; groups: T[] }[] {
  const byPeriod = new Map<string, T[]>();
  const periods: string[] = [];
  for (const group of groups) {
    const period = group.period?.trim() || "—";
    if (!byPeriod.has(period)) {
      byPeriod.set(period, []);
      periods.push(period);
    }
    byPeriod.get(period)!.push(group);
  }
  return sortQuarterPeriodsDesc(periods).map(period => ({
    period,
    groups: sortPollGroupsByLaunchDate(byPeriod.get(period)!),
  }));
}
